import { collection, doc, getDoc, setDoc, updateDoc, runTransaction } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject, listAll } from "firebase/storage";
import { uuid } from "uuidv4";
import { db, storage } from "../../../../firebase";

// Persist the attachment independently of the other draft order edits.
export const uploadSaleOrderESlip = async (order, file, currentUser) => {
  if (!order || !order.id) {
    throw new Error("Save the sale order before uploading an E-Slip.");
  }
  if (!file) {
    throw new Error("Choose an E-Slip image to upload.");
  }

  const cpapItem = (order.items || []).find(
    (item) => item.productType === "CPAP",
  );
  let fileDocRef;
  let storagePath;
  let patientFileData;

  const hn = String(order.hn || "").trim();
  if (hn) {
    if (hn.includes("/")) throw new Error("Invalid patient HN.");
    fileDocRef = doc(collection(db, "patients", hn, "cpapfile"));
    patientFileData = {
      title: "E-Slip",
      detail: `Payment proof for order ${order.receiptNo || order.id}`,
      hn,
      orderId: order.id,
      createBy: currentUser.displayName || currentUser.email || "",
      createAt: new Date(),
      cpapFile: [file.name],
    };
    storagePath = `patientcpapfiles/${hn}/${fileDocRef.id}/${file.name}`;
  } else if (cpapItem) {
    if (!cpapItem.productId) {
      throw new Error("No CPAP product found to attach files to");
    }
    fileDocRef = doc(
      collection(db, "products", cpapItem.productId, "cpapfile"),
    );
    await setDoc(fileDocRef, {
      title: "E-Slip",
      detail: `Payment proof for order ${order.receiptNo}`,
      productId: cpapItem.productId,
      createBy: currentUser.displayName,
      createAt: new Date(),
    });
    storagePath = `cpapfiles/${cpapItem.productId}/${fileDocRef.id}/${file.name}`;
  } else {
    storagePath = `saleorderfiles/${order.id}/eslips/${uuid()}/${file.name}`;
  }

  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, file);
  if (patientFileData) {
    try {
      await setDoc(fileDocRef, patientFileData);
    } catch (error) {
      try { await deleteObject(storageRef); } catch (cleanupError) { /* Keep original error. */ }
      throw error;
    }
  } else if (fileDocRef) {
    await updateDoc(fileDocRef, { cpapFile: [file.name] });
  }
  const downloadURL = await getDownloadURL(storageRef);
  await updateDoc(doc(db, "saleOrders", order.id), {
    eSlipUrl: downloadURL,
    updatedAt: new Date(),
    updatedBy: currentUser.displayName || currentUser.email || "",
  });
  return downloadURL;
};

export const matchesSaleOrderAttachment = (url, target) => {
  if (!url) return false;
  try {
    const fileRef = ref(storage, url);
    return fileRef.bucket === ref(storage).bucket && (target.directory
      ? fileRef.fullPath.startsWith(`${target.path}/`)
      : fileRef.fullPath === target.path);
  } catch (error) {
    return false;
  }
};

// Storage deletion cannot be transactional with Firestore. Keep metadata on
// failure, and tolerate a missing object so a partial deletion can be retried.
export const deleteSaleOrderAttachment = async (order, currentUser, cpapDocument) => {
  if (!order || !order.id) throw new Error("Sale order not found.");
  let target;
  let productId;
  let hn;
  let documentId;
  if (cpapDocument) {
    ({ productId, hn, id: documentId } = cpapDocument);
    if ((!productId && !hn) || !documentId) throw new Error("CPAP document not found.");
    target = { path: hn ? `patientcpapfiles/${hn}/${documentId}` : `cpapfiles/${productId}/${documentId}`, directory: true };
  } else {
    if (!order.eSlipUrl) throw new Error("No E-Slip to delete.");
    const fileRef = ref(storage, order.eSlipUrl);
    if (fileRef.bucket !== ref(storage).bucket) throw new Error("Invalid E-Slip storage location.");
    target = { path: fileRef.fullPath, directory: false };
    if (target.path.startsWith("patientcpapfiles/")) {
      [, hn, documentId] = target.path.split("/");
      if (!hn || !documentId) throw new Error("Invalid E-Slip storage location.");
    } else if (target.path.startsWith("cpapfiles/")) {
      [, productId, documentId] = target.path.split("/");
      if (!productId || !documentId) throw new Error("Invalid E-Slip storage location.");
    } else if (!target.path.startsWith(`saleorderfiles/${order.id}/eslips/`)) {
      throw new Error("Invalid E-Slip storage location.");
    }
  }
  const fileDocRef = hn
    ? doc(db, "patients", hn, "cpapfile", documentId)
    : productId ? doc(db, "products", productId, "cpapfile", documentId) : null;
  if (hn) {
    if (hn !== String(order.hn || "").trim() || hn.includes("/") || documentId.includes("/")) {
      throw new Error("The file does not belong to this order's patient.");
    }
    const fileSnapshot = await getDoc(fileDocRef);
    if (fileSnapshot.exists() && fileSnapshot.data().orderId !== order.id) {
      throw new Error("The file does not belong to this sale order.");
    }
  }
  if (!hn && productId && (!(order.items || []).some(
    (item) => item.productType === "CPAP" && item.productId === productId,
  ) || !documentId)) {
    throw new Error("The file does not belong to this order's CPAP product.");
  }

  const deleteFile = async (fileRef) => {
    try {
      await deleteObject(fileRef);
    } catch (error) {
      if (error.code !== "storage/object-not-found") throw error;
    }
  };
  const storageRef = ref(storage, target.path);
  if (target.directory) {
    const files = await listAll(storageRef);
    await Promise.all(files.items.map(deleteFile));
  } else {
    await deleteFile(storageRef);
  }

  const orderRef = doc(db, "saleOrders", order.id);
  return runTransaction(db, async (transaction) => {
    const orderSnapshot = await transaction.get(orderRef);
    const fileSnapshot = fileDocRef ? await transaction.get(fileDocRef) : null;
    let savedESlipUrl = orderSnapshot.exists() ? orderSnapshot.data().eSlipUrl || "" : "";
    // Re-read inside the transaction to preserve a concurrently saved replacement.
    if (matchesSaleOrderAttachment(savedESlipUrl, target)) {
      transaction.update(orderRef, {
        eSlipUrl: "",
        updatedAt: new Date(),
        updatedBy: currentUser.displayName || currentUser.email || "",
      });
      savedESlipUrl = "";
    }
    let remainingFiles = [];
    if (fileSnapshot && fileSnapshot.exists()) {
      remainingFiles = target.directory ? [] : (fileSnapshot.data().cpapFile || []).filter(
        (filename) => `${hn ? "patientcpapfiles" : "cpapfiles"}/${hn || productId}/${documentId}/${filename}` !== target.path,
      );
      if (remainingFiles.length) {
        transaction.update(fileDocRef, { cpapFile: remainingFiles });
      } else {
        transaction.delete(fileDocRef);
      }
    }
    return { target, savedESlipUrl, documentId, remainingFiles };
  });
};
